import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, FileText, Search, Calculator, FolderPlus, Sparkles,
} from 'lucide-react';
import { Card, ActionTile, ConfidenceMeter, Badge, SectionHeader } from '../ds/primitives';

/**
 * AskStewieAnswerHeader — the "premium" top of every Ask Stewie reply.
 * Renders:
 *   1. Question card (what the steward asked)
 *   2. AI Analysis card with confidence + short verdict
 *   3. Action Center with 5 large action tiles
 *
 * The existing 6-section structured answer renders BELOW this header
 * inside the existing StructuredAnswerCard — preserving every behavior.
 */
export default function AskStewieAnswerHeader({ question, answer, onSaveToCase, sticky = false }) {
  const navigate = useNavigate();
  if (!answer || typeof answer !== 'object') return null;

  const primary = answer.primary_issue || answer.detected_issues?.[0] || 'general';
  // Confidence: prefer numeric `confidence` (0-1), else `confidence_score` (0-1),
  // else `confidence_label` ("high"/"medium"/"low"), else infer from response mode.
  const conf = computeConfidence(answer);
  const mode = answer.mode || answer.response_mode || 'general_guidance_clarify';
  const verdict = pickVerdict(answer);

  return (
    <div className="asaw-root asds-anim-fade-up" data-testid="askstewie-answer-header">
      <style>{styles}</style>

      {/* 1. Question card */}
      {question && (
        <Card className="asaw-question" data-testid="asaw-question-card">
          <div className="asaw-q-eyebrow">YOUR QUESTION</div>
          <div className="asaw-q-text">{question}</div>
        </Card>
      )}

      {/* 2. AI Analysis card */}
      <Card className="asaw-analysis" glow="blue" data-testid="asaw-analysis-card">
        <div className="asaw-analysis-head">
          <div className="asaw-analysis-eyebrow">
            <Sparkles size={12} />
            <span>AI ANALYSIS</span>
          </div>
          <ConfidenceMeter value={conf} />
        </div>
        <div className="asaw-analysis-verdict">{verdict.title}</div>
        <div className="asaw-analysis-sub">{verdict.subtitle}</div>
        <div className="asaw-analysis-tags">
          <Badge tone="purple" size="lg">{prettyIssue(primary)}</Badge>
          {answer.secondary_issue && answer.secondary_issue !== primary && (
            <Badge tone="muted" size="sm">+ {prettyIssue(answer.secondary_issue)}</Badge>
          )}
          <Badge tone="cyan" size="sm">{prettyMode(mode)}</Badge>
        </div>
      </Card>

      {/* 3. Action Center */}
      <div className="asaw-actions" data-testid="asaw-action-center">
        <SectionHeader eyebrow="ACTION CENTER" title="What would you like to do next?" />
        <div className="asaw-actions-grid">
          <ActionTile
            testId="action-understand-rule"
            icon={BookOpen}
            tone="cyan"
            title="Understand the Rule"
            hint="See the contract & MOA"
            onClick={() => scrollToFirst([
              '[data-testid="exact-contract-wording-block"]',
              '[data-testid="six-section-contract_language"]',
            ])}
          />
          <ActionTile
            testId="action-build-grievance"
            icon={FileText}
            tone="purple"
            title="Build Grievance"
            hint="Open Grievance Builder"
            onClick={() => navigate('/grievance-builder')}
          />
          <ActionTile
            testId="action-gather-evidence"
            icon={Search}
            tone="yellow"
            title="Gather Evidence"
            hint="Information requests"
            onClick={() => scrollToFirst([
              '[data-testid="information-requests-section"]',
              '[data-testid="six-section-evidence_needed"]',
            ])}
          />
          <ActionTile
            testId="action-calculate-remedy"
            icon={Calculator}
            tone="orange"
            title="Calculate Remedy"
            hint="Make the grievant whole"
            onClick={() => scrollToFirst([
              '[data-testid="six-section-recommended_remedy"]',
            ])}
          />
          <ActionTile
            testId="action-save-to-case"
            icon={FolderPlus}
            tone="green"
            title="Save to Case Locker"
            hint="Track & build the file"
            onClick={onSaveToCase || (() => navigate('/case-locker'))}
          />
        </div>
      </div>
    </div>
  );
}

function scrollToFirst(selectors) {
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a brief highlight pulse so the user sees what they jumped to
      el.classList.add('asaw-target-pulse');
      setTimeout(() => el.classList.remove('asaw-target-pulse'), 1800);
      return true;
    }
  }
  return false;
}

function prettyIssue(slug) {
  if (!slug) return 'General';
  return String(slug).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function prettyMode(mode) {
  const map = {
    contract_grounded: 'Contract Grounded',
    general_guidance_clarify: 'Guidance · Needs Clarify',
    clarify_only: 'Needs Clarification',
  };
  return map[mode] || mode.replace(/_/g, ' ');
}

function pickVerdict(answer) {
  // Compose a 1-line verdict from existing answer fields without inventing facts.
  const issue = prettyIssue(answer.primary_issue || answer.detected_issues?.[0] || 'general');
  if (answer.short_summary)          return { title: answer.short_summary, subtitle: '' };
  if (answer.explanation)            return { title: stripFirstSentence(answer.explanation), subtitle: '' };
  if (answer.kid_explanation)        return { title: answer.kid_explanation, subtitle: '' };
  if (answer.what_went_wrong)        return { title: `Possible ${issue} situation detected.`, subtitle: stripFirstSentence(answer.what_went_wrong) };
  return { title: `Possible ${issue} situation detected.`, subtitle: 'Tap any action below to dig deeper.' };
}
function stripFirstSentence(text) {
  if (!text) return '';
  const s = String(text).trim();
  const m = s.match(/^(.+?[.!?])\s/);
  return m ? m[1] : (s.length > 180 ? s.slice(0, 180) + '…' : s);
}

function computeConfidence(answer) {
  // Numeric override (already 0..1)
  if (typeof answer.confidence === 'number' && answer.confidence > 0) {
    return Math.round(answer.confidence * 100);
  }
  if (typeof answer.confidence_score === 'number' && answer.confidence_score > 0) {
    return Math.round(answer.confidence_score * 100);
  }
  // Label
  const label = String(answer.confidence_label || '').toLowerCase();
  if (label === 'high')   return 88;
  if (label === 'medium' || label === 'med') return 70;
  if (label === 'low')    return 48;
  // Derive from response mode + presence of grounding signals
  const mode = String(answer.mode || answer.response_mode || '').toLowerCase();
  const hasQuote = !!(answer.exact_contract_wording && String(answer.exact_contract_wording).trim().length > 20);
  const sourceCount = Array.isArray(answer.sources) ? answer.sources.length : 0;
  if (mode === 'contract_grounded' && hasQuote) return 92;
  if (mode === 'contract_grounded')             return 80;
  if (mode === 'clarify_only')                  return 50;
  // general_guidance_clarify: score by source breadth
  if (sourceCount >= 4) return 78;
  if (sourceCount >= 2) return 68;
  if (sourceCount >= 1) return 60;
  return 55;
}

const styles = `
.asaw-root {
  display: flex; flex-direction: column; gap: 16px;
  margin-bottom: 16px;
  font-family: var(--asds-font-sans);
}

.asaw-question {
  padding: 12px 16px;
  background: var(--asds-bg-glass);
  border-left: 3px solid var(--asds-cyan);
  border-radius: var(--asds-r-md);
}
.asaw-q-eyebrow {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.12em;
  color: var(--asds-text-secondary); text-transform: uppercase; margin-bottom: 4px;
}
.asaw-q-text {
  font-size: 15px; font-weight: 600; color: var(--asds-text-primary);
  line-height: 1.4;
}

.asaw-analysis {
  padding: 18px 18px 16px;
  background:
    radial-gradient(120% 80% at 0% 0%, rgba(37,99,235,0.10) 0%, transparent 60%),
    var(--asds-bg-elevated);
  border-color: rgba(37,99,235,0.25);
}
.asaw-analysis-head {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin-bottom: 10px;
}
.asaw-analysis-eyebrow {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.14em;
  color: var(--asds-cyan); text-transform: uppercase;
}
.asaw-analysis-verdict {
  font-size: 18px; font-weight: 800; color: var(--asds-text-primary);
  letter-spacing: -0.015em; line-height: 1.25;
}
.asaw-analysis-sub {
  margin-top: 4px;
  font-size: 13px; color: var(--asds-text-secondary); line-height: 1.45;
}
.asaw-analysis-tags {
  margin-top: 12px;
  display: flex; flex-wrap: wrap; gap: 6px;
}

.asaw-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (min-width: 600px) {
  .asaw-actions-grid { grid-template-columns: repeat(5, 1fr); }
}

/* Highlight pulse applied briefly to scroll-target sections so the user
   sees exactly what they jumped to. */
@keyframes asaw-target-pulse-kf {
  0%   { box-shadow: 0 0 0 0 rgba(34,211,238,0.55), 0 0 0 0 rgba(34,211,238,0.0); }
  50%  { box-shadow: 0 0 0 4px rgba(34,211,238,0.40), 0 0 28px 4px rgba(34,211,238,0.35); }
  100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.0),  0 0 0 0 rgba(34,211,238,0.0); }
}
.asaw-target-pulse {
  animation: asaw-target-pulse-kf 1.6s ease-out;
  border-radius: 12px;
}
`;
